import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';

/**
 * Catches render-time errors anywhere below it and shows a recoverable fallback.
 *
 * Without a boundary, a single throw during render unmounts the entire React tree
 * and the user is left on a blank white page with no indication of what happened.
 * The most common trigger is passing a non-string (e.g. an API error object) where
 * a React child is expected.
 *
 * `resetKey` clears the error when it changes - wire it to the current route so
 * navigating away from a broken screen recovers the app without a full reload.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Unhandled UI error:', error, errorInfo);
    }

    componentDidUpdate(prevProps) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false, error: null });
        }
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    backgroundColor: 'background.default',
                }}
            >
                <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
                    <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Something went wrong
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This screen ran into an unexpected problem. Reloading should fix it - any
                        unsaved changes on this screen will be lost. If it keeps happening, send
                        this message to support.
                    </Typography>
                    {this.state.error?.message && (
                        <Typography
                            variant="caption"
                            component="pre"
                            sx={{
                                display: 'block',
                                mb: 3,
                                p: 1.5,
                                textAlign: 'left',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                backgroundColor: 'action.hover',
                                borderRadius: 1,
                                color: 'text.secondary',
                            }}
                        >
                            {this.state.error.message}
                        </Typography>
                    )}
                    <Button variant="contained" onClick={this.handleReload}>
                        Reload page
                    </Button>
                </Paper>
            </Box>
        );
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node,
    // Changing this value clears the error - pass the current route to recover on navigate.
    resetKey: PropTypes.string,
};

export default ErrorBoundary;
